import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [comic, setComic] = useState<Comic | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const found = comics.find(c => c.id === id);
    if (found) {
      setComic(found);
      setNotFound(false);
    } else {
      setNotFound(true);
    }
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-background-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-file-warning-line text-4xl text-foreground-400"></i>
          </div>
          <h1 className="text-2xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您要查看的漫画不存在或已被删除</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50 pt-16 flex items-center justify-center">
        <div className="flex items-center gap-3 text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-xl"></i>
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  const relatedComics = comics
    .filter(c => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 6);

  const sortedChapters = [...comic.chapters].sort((a, b) =>
    sortOrder === 'asc' ? a.number - b.number : b.number - a.number
  );

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'ComicSeries',
    name: comic.title,
    author: { '@type': 'Person', name: comic.author },
    description: comic.description,
    url: `https://hahamanhua.com/article/${comic.id}`,
    image: comic.cover,
    genre: comic.category,
    datePublished: comic.chapters.length > 0 ? comic.chapters[0].updatedAt : comic.updatedAt,
    dateModified: comic.updatedAt,
    inLanguage: 'zh-CN',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: comic.rating,
      bestRating: '10',
      ratingCount: Math.floor(comic.views / 100),
    },
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: 'https://schema.org/ReadAction',
      userInteractionCount: comic.views,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hahamanhua.com' },
      { '@type': 'ListItem', position: 2, name: comic.category, item: `https://hahamanhua.com/category/${comic.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: comic.title, item: `https://hahamanhua.com/article/${comic.id}` },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <article>
        <div className="bg-white border-b border-foreground-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${comic.categorySlug}`} className="hover:text-primary-600 transition-colors">{comic.category}</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-900 font-medium">{comic.title}</span>
            </nav>

            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-64 flex-shrink-0">
                <div className="aspect-[5/7] rounded-xl overflow-hidden bg-background-100">
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    title={`${comic.title} - 韩国漫画`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-4">{comic.title}</h1>

                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="text-sm text-foreground-500">分类：{comic.category}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className={`text-sm font-medium ${comic.status === '连载中' ? 'text-green-600' : 'text-foreground-500'}`}>
                    {comic.status}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span className="text-sm text-foreground-500">最近更新：{comic.updatedAt}</span>
                </div>

                <div className="flex items-center gap-6 mb-5">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-star-fill text-amber-500"></i>
                    <span className="text-lg font-bold text-foreground-900">{comic.rating}</span>
                    <span className="text-xs text-foreground-400">/ 10</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-foreground-500">
                    <i className="ri-eye-line"></i>
                    <span className="text-sm">
                      {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 阅读
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-foreground-500">
                    <i className="ri-file-list-3-line"></i>
                    <span className="text-sm">{comic.chapters.length} 话</span>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap mb-5">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  {comic.description}
                </p>

                <Link
                  to={`/article/${comic.id}/read/1`}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  开始阅读 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-foreground-900">
              章节列表 <span className="text-sm font-normal text-foreground-500">（共 {comic.chapters.length} 话）</span>
            </h2>
            <button
              onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
              className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <i className={sortOrder === 'asc' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}></i>
              {sortOrder === 'asc' ? '正序' : '倒序'}
            </button>
          </div>

          <div className="bg-white rounded-xl p-4 md:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {sortedChapters.map((chapter) => (
                <Link
                  key={chapter.id}
                  to={`/article/${comic.id}/read/${chapter.number}`}
                  className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-background-50 hover:bg-primary-50 hover:text-primary-700 transition-colors group cursor-pointer"
                >
                  <span className="text-sm font-medium truncate">{chapter.title}</span>
                  <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-500 flex-shrink-0 ml-2"></i>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {relatedComics.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
            <h2 className="text-lg font-bold text-foreground-900 mb-4">相关推荐</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {relatedComics.map((rc: Comic) => (
                <ComicCard key={rc.id} comic={rc} size="small" />
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}